import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (filename: string, includeBuyboxVariants?: boolean) => void;
  defaultFilename: string;
}

const ExportModal: React.FC<ExportModalProps> = ({ isOpen, onClose, onExport, defaultFilename }) => {
  const { t } = useTranslation();
  const [filename, setFilename] = useState(defaultFilename);
  const [includeBuyboxVariants, setIncludeBuyboxVariants] = useState(false);
  
  // Actualizar el filename cuando cambie el defaultFilename o se abra el modal
  useEffect(() => {
    if (isOpen) {
      setFilename(defaultFilename);
    }
  }, [defaultFilename, isOpen]);

  if (!isOpen) return null;

  const handleExport = () => {
    onExport(filename, includeBuyboxVariants);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96 max-w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{t('export.title', 'Exportar a Excel')}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('export.filename', 'Nombre del archivo')}
          </label>
          <input
            type="text"
            value={filename}
            onChange={(e) => setFilename(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder={t('export.filenamePlaceholder', 'productos_exportados')}
          />
          <p className="text-xs text-gray-500 mt-1">
            {t('export.filenameHint', 'La extensión .xlsx se agregará automáticamente')}
          </p>
        </div>
        
        <div className="mb-4">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeBuyboxVariants}
              onChange={(e) => setIncludeBuyboxVariants(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">
              {t('export.includeBuyboxVariants', 'Incluir variantes de BuyBox')}
            </span>
          </label>
          <p className="text-xs text-gray-500 mt-1 ml-6">
            {t('export.buyboxVariantsHint', 'Las variantes se mostrarán debajo de cada producto principal')}
          </p>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('export.cancel', 'Cancelar')}
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            {t('export.export', 'Exportar')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;